/**
 * Catalog of legal document templates for PreLegal.cl.
 *
 * Each template defines:
 *  - id, name, icon, description (for the picker)
 *  - category (for grouping in the modal)
 *  - fields[]: { key, label, placeholder, type ('text'|'textarea'|'date'), required }
 *  - buildPrompt(data): returns the natural-language prompt that will be sent to /legal/ask
 *
 * The prompt is crafted so the assistant:
 *  - drafts the formal document in Chilean court style
 *  - cites only verifiable articles (crosscheck + anti-sycophancy already enforce this)
 *  - wraps the draft in a markdown block so the existing auto-.docx detector triggers
 */

// Utility: clean "—" when a field is empty so the prompt reads naturally
const f = (v, fallback = "[no especificado]") => {
  const s = (v || "").toString().trim();
  return s ? s : fallback;
};

const LEGAL_TEMPLATES = [
  // -------------------------------------------------------------------
  // CIVIL
  // -------------------------------------------------------------------
  {
    id: "demanda_civil",
    name: "Demanda civil genérica",
    icon: "⚖️",
    category: "Civil",
    description: "Escrito inicial para iniciar un juicio civil ordinario o sumario.",
    fields: [
      { key: "tribunal", label: "Tribunal", placeholder: "Ej: 1° Juzgado Civil de Santiago", required: true },
      { key: "demandante_nombre", label: "Nombre del demandante", placeholder: "Juan Pérez González", required: true },
      { key: "demandante_rut", label: "RUT del demandante", placeholder: "12.345.678-9", required: true },
      { key: "demandante_domicilio", label: "Domicilio del demandante", placeholder: "Calle X N° 123, Santiago" },
      { key: "demandado_nombre", label: "Nombre del demandado", placeholder: "María Soto Rojas", required: true },
      { key: "demandado_rut", label: "RUT del demandado", placeholder: "15.432.987-6" },
      { key: "demandado_domicilio", label: "Domicilio del demandado", placeholder: "Calle Y N° 456" },
      { key: "materia", label: "Materia / causa de pedir", placeholder: "Ej: incumplimiento de contrato, cobro de pesos, indemnización de perjuicios", required: true },
      { key: "hechos", label: "Relato de los hechos", placeholder: "Describe cronológicamente qué ocurrió", type: "textarea", required: true },
      { key: "pretension", label: "Pretensión / lo que pides al tribunal", placeholder: "Ej: que se condene al demandado a pagar $5.000.000 más intereses y costas", type: "textarea", required: true },
    ],
    buildPrompt: (d) => `Redáctame una DEMANDA CIVIL formal en estilo de tribunal chileno con los siguientes datos:

Tribunal: ${f(d.tribunal)}
Demandante: ${f(d.demandante_nombre)}, RUT ${f(d.demandante_rut)}, domiciliado en ${f(d.demandante_domicilio)}
Demandado: ${f(d.demandado_nombre)}, RUT ${f(d.demandado_rut)}, domiciliado en ${f(d.demandado_domicilio)}
Materia: ${f(d.materia)}

HECHOS:
${f(d.hechos)}

PRETENSIÓN:
${f(d.pretension)}

Estructura esperada: suma, capítulo de los hechos numerados, el derecho (citando SOLO artículos que puedas verificar del Código Civil o del Código de Procedimiento Civil chileno), peticiones concretas (POR TANTO, EN SUBSIDIO), otrosíes (patrocinio y poder, acompaña documentos). Formato formal con encabezados en MAYÚSCULAS. Si detectas que la acción está prescrita o que algún plazo ya venció dado la fecha de hoy, adviérteme ANTES de redactar.`,
  },

  {
    id: "carta_notarial_arriendo",
    name: "Carta notarial por alza de renta",
    icon: "🏠",
    category: "Civil",
    description: "Carta formal para impugnar un alza no pactada de la renta arrendaticia.",
    fields: [
      { key: "arrendatario_nombre", label: "Tu nombre (arrendatario)", placeholder: "Juan Pérez", required: true },
      { key: "arrendatario_rut", label: "Tu RUT", placeholder: "12.345.678-9", required: true },
      { key: "arrendador_nombre", label: "Nombre del arrendador", placeholder: "María Soto", required: true },
      { key: "direccion_inmueble", label: "Dirección del inmueble arrendado", placeholder: "Calle X 123, Providencia", required: true },
      { key: "fecha_contrato", label: "Fecha de firma del contrato", placeholder: "15 de enero de 2025", type: "date" },
      { key: "plazo_contrato", label: "Plazo del contrato", placeholder: "Ej: 2 años, vence en enero 2027" },
      { key: "renta_original", label: "Renta pactada", placeholder: "$400.000 mensuales", required: true },
      { key: "renta_nueva", label: "Nueva renta exigida", placeholder: "$600.000 mensuales", required: true },
      { key: "fecha_alza", label: "Fecha del alza", placeholder: "1 de marzo de 2026" },
      { key: "detalles_extra", label: "Otros detalles (cortes de servicios, amenazas, etc.)", placeholder: "Opcional", type: "textarea" },
    ],
    buildPrompt: (d) => `Redáctame una CARTA NOTARIAL dirigida al arrendador para impugnar un alza unilateral de renta. Debe ser formal, firme y jurídicamente bien fundada.

Datos:
- Arrendatario: ${f(d.arrendatario_nombre)}, RUT ${f(d.arrendatario_rut)}
- Arrendador destinatario: ${f(d.arrendador_nombre)}
- Inmueble: ${f(d.direccion_inmueble)}
- Contrato firmado el: ${f(d.fecha_contrato)}, plazo: ${f(d.plazo_contrato)}
- Renta pactada: ${f(d.renta_original)}
- Alza exigida: ${f(d.renta_nueva)} a partir del ${f(d.fecha_alza)}
- Otros detalles: ${f(d.detalles_extra, "ninguno")}

Cita SOLO artículos que puedas verificar (arts. relevantes del Código Civil y Ley 18.101). Incluye:
(1) identificación de las partes y del contrato,
(2) los hechos constitutivos del alza indebida,
(3) el fundamento legal,
(4) el requerimiento formal de revertir el alza y mantener la renta pactada,
(5) plazo razonable para respuesta (ej. 10 días hábiles),
(6) advertencia de acciones legales civiles o penales en caso de persistir la conducta,
(7) firma.
Formato de carta notarial chilena.`,
  },

  // -------------------------------------------------------------------
  // PENAL
  // -------------------------------------------------------------------
  {
    id: "querella",
    name: "Querella criminal",
    icon: "⚠️",
    category: "Penal",
    description: "Escrito para iniciar una acción penal por un delito específico.",
    fields: [
      { key: "tribunal", label: "Juzgado de Garantía", placeholder: "Ej: Juzgado de Garantía de Santiago", required: true },
      { key: "querellante_nombre", label: "Nombre del querellante", placeholder: "Juan Pérez", required: true },
      { key: "querellante_rut", label: "RUT del querellante", placeholder: "12.345.678-9", required: true },
      { key: "querellado_nombre", label: "Nombre del querellado (o 'quienes resulten responsables')", placeholder: "María Soto o 'NN'", required: true },
      { key: "delito", label: "Delito imputado", placeholder: "Ej: estafa, amenazas, injurias, lesiones", required: true },
      { key: "fecha_hechos", label: "Fecha aproximada de los hechos", placeholder: "Marzo de 2026", type: "date" },
      { key: "lugar_hechos", label: "Lugar de los hechos", placeholder: "Comuna, calle" },
      { key: "hechos", label: "Relato de los hechos", placeholder: "Qué pasó, cómo, con quiénes, pruebas disponibles", type: "textarea", required: true },
      { key: "pruebas", label: "Medios de prueba (opcional)", placeholder: "Testigos, documentos, informes, grabaciones", type: "textarea" },
    ],
    buildPrompt: (d) => `Redáctame una QUERELLA CRIMINAL formal según el Código Procesal Penal chileno.

Datos:
- Juzgado de Garantía: ${f(d.tribunal)}
- Querellante: ${f(d.querellante_nombre)}, RUT ${f(d.querellante_rut)}
- Querellado: ${f(d.querellado_nombre)}
- Delito imputado: ${f(d.delito)}
- Fecha de los hechos: ${f(d.fecha_hechos)}, lugar: ${f(d.lugar_hechos)}

HECHOS:
${f(d.hechos)}

MEDIOS DE PRUEBA OFRECIDOS:
${f(d.pruebas, "por definir")}

Cumple con los requisitos del art. 113 del Código Procesal Penal (individualización, relación circunstanciada de los hechos, participación que se atribuye, etc.). Cita SOLO artículos que puedas verificar del CPP y del Código Penal. Incluye suma, cuerpo de la querella, diligencias que solicitas, peticiones concretas y otrosíes (patrocinio y poder). IMPORTANTE: antes de redactar, verifica si el delito tiene prescripción y si los hechos están dentro de plazo considerando la fecha de hoy. Si está prescrito, adviértemelo y no redactes el escrito.`,
  },

  {
    id: "ampliacion_peritaje",
    name: "Solicitud de ampliación de peritaje",
    icon: "🔬",
    category: "Penal",
    description: "Petición al tribunal para que se amplíe o complemente un peritaje ya rendido.",
    fields: [
      { key: "tribunal", label: "Tribunal / RUC / RIT", placeholder: "RIT 1234-2026, Juzgado de Garantía de Viña del Mar", required: true },
      { key: "solicitante_nombre", label: "Nombre del solicitante", placeholder: "Juan Pérez", required: true },
      { key: "solicitante_rut", label: "RUT del solicitante", placeholder: "12.345.678-9", required: true },
      { key: "calidad", label: "Calidad procesal", placeholder: "Ej: querellante, defensor, ministerio público", required: true },
      { key: "perito_nombre", label: "Nombre del perito / institución", placeholder: "Dr. X o SML, LABOCAR, etc." },
      { key: "peritaje_existente", label: "Descripción del peritaje existente", placeholder: "Informe pericial de fecha... que concluyó...", type: "textarea", required: true },
      { key: "puntos_ampliacion", label: "Puntos específicos a ampliar o aclarar", placeholder: "Listado concreto de lo que falta o lo que debe precisarse", type: "textarea", required: true },
      { key: "motivo", label: "Motivo / pertinencia", placeholder: "Por qué esta ampliación es necesaria para el esclarecimiento", type: "textarea" },
    ],
    buildPrompt: (d) => `Redáctame una SOLICITUD DE AMPLIACIÓN DE PERITAJE dirigida al tribunal dentro de un proceso penal en Chile.

Causa: ${f(d.tribunal)}
Solicitante: ${f(d.solicitante_nombre)}, RUT ${f(d.solicitante_rut)}, en calidad de ${f(d.calidad)}
Perito/institución: ${f(d.perito_nombre)}

PERITAJE EXISTENTE:
${f(d.peritaje_existente)}

PUNTOS A AMPLIAR O ACLARAR:
${f(d.puntos_ampliacion)}

MOTIVO DE LA SOLICITUD:
${f(d.motivo, "el esclarecimiento de los hechos lo hace necesario.")}

Cita SOLO artículos verificables del Código Procesal Penal (el crosscheck del sistema te confirmará qué artículos existen). Estructura: suma, individualización, antecedentes del peritaje, fundamento, petición concreta, otrosíes si corresponden. Tono formal de tribunal.`,
  },

  // -------------------------------------------------------------------
  // CONSUMIDOR / ADMINISTRATIVO
  // -------------------------------------------------------------------
  {
    id: "reclamo_sernac",
    name: "Reclamo SERNAC",
    icon: "🛒",
    category: "Consumidor",
    description: "Reclamo formal ante el SERNAC por infracción a la Ley del Consumidor.",
    fields: [
      { key: "consumidor_nombre", label: "Tu nombre", placeholder: "Juan Pérez", required: true },
      { key: "consumidor_rut", label: "Tu RUT", placeholder: "12.345.678-9", required: true },
      { key: "consumidor_email", label: "Tu correo electrónico", placeholder: "juan@ejemplo.cl" },
      { key: "proveedor_nombre", label: "Empresa / proveedor", placeholder: "Ej: Tienda X S.A.", required: true },
      { key: "proveedor_rut", label: "RUT del proveedor (si lo conoces)", placeholder: "99.999.999-9" },
      { key: "producto_servicio", label: "Producto o servicio contratado", placeholder: "Ej: refrigerador marca Y, servicio de internet", required: true },
      { key: "fecha_compra", label: "Fecha de compra / contratación", placeholder: "10 de febrero de 2026", type: "date" },
      { key: "monto", label: "Monto involucrado", placeholder: "$450.000" },
      { key: "problema", label: "Descripción del problema", placeholder: "Qué falló, qué prometieron, qué entregaron", type: "textarea", required: true },
      { key: "gestiones_previas", label: "Gestiones previas con el proveedor", placeholder: "Llamadas, correos, tickets", type: "textarea" },
      { key: "solicitud", label: "Qué solicitas", placeholder: "Reembolso, reparación, cambio, compensación...", required: true },
    ],
    buildPrompt: (d) => `Redáctame un RECLAMO FORMAL ante el SERNAC según la Ley 19.496 de Protección del Consumidor.

Datos del consumidor: ${f(d.consumidor_nombre)}, RUT ${f(d.consumidor_rut)}, correo ${f(d.consumidor_email, "(no informado)")}
Proveedor: ${f(d.proveedor_nombre)}, RUT ${f(d.proveedor_rut, "(desconocido)")}
Producto/servicio: ${f(d.producto_servicio)}
Fecha de compra: ${f(d.fecha_compra)}, monto: ${f(d.monto, "(no especificado)")}

PROBLEMA:
${f(d.problema)}

GESTIONES PREVIAS:
${f(d.gestiones_previas, "ninguna")}

SOLICITUD: ${f(d.solicitud)}

Redacta en tono formal pero claro. Cita las disposiciones de la Ley 19.496 aplicables (no inventes números, si no estás seguro di simplemente "conforme a la Ley del Consumidor"). Incluye: encabezado al SERNAC, identificación de las partes, relato de los hechos, infracciones imputadas, solicitud concreta, acompaña documentos.`,
  },

  {
    id: "denuncia_policia_local",
    name: "Denuncia Juzgado de Policía Local",
    icon: "🏛️",
    category: "Consumidor",
    description: "Denuncia ante JPL por infracción a la Ley del Consumidor u otras faltas.",
    fields: [
      { key: "tribunal", label: "Juzgado de Policía Local", placeholder: "JPL de la comuna respectiva", required: true },
      { key: "denunciante_nombre", label: "Tu nombre", placeholder: "Juan Pérez", required: true },
      { key: "denunciante_rut", label: "Tu RUT", placeholder: "12.345.678-9", required: true },
      { key: "denunciante_domicilio", label: "Tu domicilio", placeholder: "Calle X 123, comuna Y" },
      { key: "denunciado_nombre", label: "Empresa o persona denunciada", placeholder: "Tienda X S.A.", required: true },
      { key: "denunciado_direccion", label: "Dirección del denunciado", placeholder: "Calle Comercial 456" },
      { key: "infraccion", label: "Tipo de infracción", placeholder: "Ej: publicidad engañosa, negativa injustificada de venta", required: true },
      { key: "hechos", label: "Relato de los hechos", placeholder: "Qué pasó, cuándo, cómo", type: "textarea", required: true },
      { key: "pruebas", label: "Pruebas que acompañas", placeholder: "Boleta, fotos, correos, testigos" },
    ],
    buildPrompt: (d) => `Redáctame una DENUNCIA FORMAL ante el Juzgado de Policía Local.

Tribunal: ${f(d.tribunal)}
Denunciante: ${f(d.denunciante_nombre)}, RUT ${f(d.denunciante_rut)}, domicilio ${f(d.denunciante_domicilio)}
Denunciado: ${f(d.denunciado_nombre)}, domicilio ${f(d.denunciado_direccion)}
Infracción imputada: ${f(d.infraccion)}

HECHOS:
${f(d.hechos)}

PRUEBAS:
${f(d.pruebas, "acompañaré las que correspondan")}

Cita disposiciones relevantes de la Ley 19.496 (del consumidor) o Ley 18.287 de Juzgados de Policía Local según corresponda. Estructura formal de denuncia: suma, cuerpo con identificación de las partes, relato cronológico, fundamento legal, petición concreta, acompaña documentos.`,
  },

  // -------------------------------------------------------------------
  // RECURSOS / ADMIN
  // -------------------------------------------------------------------
  {
    id: "apelacion",
    name: "Recurso de apelación",
    icon: "↗️",
    category: "Recursos",
    description: "Recurso ordinario para revisar una sentencia de primera instancia.",
    fields: [
      { key: "tribunal_primera", label: "Tribunal de origen (primera instancia)", placeholder: "2° Juzgado Civil de Santiago", required: true },
      { key: "tribunal_alzada", label: "Tribunal de alzada", placeholder: "I. Corte de Apelaciones de Santiago" },
      { key: "causa", label: "ROL / RIT / RUC de la causa", placeholder: "Ej: C-1234-2026", required: true },
      { key: "apelante_nombre", label: "Apelante", placeholder: "Juan Pérez", required: true },
      { key: "apelante_rut", label: "RUT del apelante", placeholder: "12.345.678-9", required: true },
      { key: "fecha_sentencia", label: "Fecha de la sentencia apelada", placeholder: "15 de marzo de 2026", type: "date", required: true },
      { key: "resumen_sentencia", label: "Resumen de lo resuelto por el tribunal", placeholder: "Qué decidió y por qué", type: "textarea", required: true },
      { key: "agravios", label: "Agravios / errores que reclamas", placeholder: "Listado concreto de errores de hecho o de derecho", type: "textarea", required: true },
      { key: "peticion", label: "Qué pides a la Corte", placeholder: "Ej: revocar íntegramente la sentencia y acoger la demanda", required: true },
    ],
    buildPrompt: (d) => `Redáctame un RECURSO DE APELACIÓN formal en estilo chileno.

Tribunal de origen: ${f(d.tribunal_primera)}
Tribunal de alzada: ${f(d.tribunal_alzada, "la Corte de Apelaciones respectiva")}
Causa: ${f(d.causa)}
Apelante: ${f(d.apelante_nombre)}, RUT ${f(d.apelante_rut)}
Sentencia apelada: dictada el ${f(d.fecha_sentencia)}

RESUMEN DE LO RESUELTO:
${f(d.resumen_sentencia)}

AGRAVIOS (errores de hecho o derecho que se reclaman):
${f(d.agravios)}

PETICIÓN CONCRETA: ${f(d.peticion)}

Incluye: suma, individualización, antecedentes, agravios numerados (punto por punto), el derecho invocado (cita SOLO artículos verificables), petición concreta a la Corte, otrosí con copia a la contraria. Tono formal. ANTES de redactar verifica si el recurso está dentro de plazo (5 días hábiles desde notificación en civil, o el que corresponda) considerando la fecha de hoy y la fecha de la sentencia; si está fuera de plazo, adviérteme y NO redactes.`,
  },

  {
    id: "descargo_administrativo",
    name: "Escrito de descargo administrativo",
    icon: "📋",
    category: "Recursos",
    description: "Descargos ante un sumario o procedimiento sancionatorio administrativo.",
    fields: [
      { key: "organismo", label: "Organismo que instruye", placeholder: "Ej: Municipalidad de X, Contraloría, SII", required: true },
      { key: "rol_expediente", label: "N° de expediente / sumario", placeholder: "Sumario N° 123/2026" },
      { key: "investigado_nombre", label: "Tu nombre", placeholder: "Juan Pérez", required: true },
      { key: "investigado_rut", label: "Tu RUT", placeholder: "12.345.678-9", required: true },
      { key: "cargo_formulado", label: "Cargo o infracción formulada", placeholder: "Qué te imputan", type: "textarea", required: true },
      { key: "descargos", label: "Tus descargos / defensa", placeholder: "Por qué no ocurrió, o por qué no es sancionable", type: "textarea", required: true },
      { key: "pruebas", label: "Medios de prueba que ofreces", placeholder: "Documentos, testigos" },
    ],
    buildPrompt: (d) => `Redáctame un ESCRITO DE DESCARGOS ante un procedimiento administrativo sancionatorio.

Organismo: ${f(d.organismo)}
Expediente: ${f(d.rol_expediente, "(no indicado)")}
Investigado: ${f(d.investigado_nombre)}, RUT ${f(d.investigado_rut)}

CARGO FORMULADO:
${f(d.cargo_formulado)}

DESCARGOS:
${f(d.descargos)}

MEDIOS DE PRUEBA:
${f(d.pruebas, "por definir")}

Estructura: suma (formula descargos), individualización, los cargos que se contestan, los descargos punto por punto, los fundamentos de derecho (cita con cuidado; si no estás seguro de un artículo prefiere "las normas aplicables"), ofrece pruebas, peticiones concretas. Tono formal administrativo chileno.`,
  },
];

export default LEGAL_TEMPLATES;
